import { useAcessoStore, temAtribuicaoRbac, type AcessoResumo } from '../store/acesso.store';

/**
 * [ADR-069 §9 regra 5 / F4.3 #890] `usePermissao()` — filtro de UX (menu, telas, botões).
 * A proteção real continua sendo o backend (F5); aqui é só esconder/desabilitar o que o
 * usuário não pode fazer, pra não oferecer porta que não abre.
 *
 * Regra de leitura (espelha `resumirAcesso`, ver o comentário lá): permissão de nível
 * TENANT só conta se estiver em `permissoes.tenant_wide`; permissão de nível
 * ESTABELECIMENTO conta em `tenant_wide` OU no estabelecimento pedido — sem um
 * estabelecimento específico (não há estado global de "unidade ativa" hoje, D10 #915),
 * conta em QUALQUER unidade onde o usuário tenha o código (decisão de UX: mostrar o item se
 * ele serve pra pelo menos uma unidade; o backend recorta de verdade quando a rota carrega).
 *
 * [Limitação conhecida] O catálogo (nível TENANT vs ESTABELECIMENTO por código) não é
 * exposto ao frontend de forma genérica ainda — `permissoes.catalogo.ts` no backend é a
 * fonte real. `RECURSOS_NIVEL_TENANT` abaixo é uma cópia manual dos recursos nível TENANT
 * hoje; se um recurso novo nascer TENANT, esta lista precisa ganhar uma linha (do
 * contrário o código cai no ramo ESTABELECIMENTO, mais permissivo — ainda seguro, porque
 * o backend continua sendo a autoridade real, mas o menu poderia mostrar um item que
 * some ao clicar antes do enforce chegar naquela rota).
 *
 * [ADR-069 §13.24 / #891 D-nivel-tenant-consultar] `crm.contato` entrou nesta lista nesta
 * entrega (antes ficava de fora por omissão — divergência pré-existente do backend, que
 * SEMPRE tratou `crm.contato` como TENANT; o frontend, por nunca ter copiado o recurso pra
 * cá, já se comportava de forma mais permissiva "por acidente"). `crm.anotacao` nunca
 * precisou entrar — o catálogo back agora também o marca ESTABELECIMENTO (mudança de nível,
 * não deste mecanismo), então o ramo abaixo já trata os dois do mesmo jeito.
 */
const RECURSOS_NIVEL_TENANT = new Set([
  'config.empresa', 'config.assinatura', 'config.proposta_segmento', 'config.nota_fiscal',
  'config.estabelecimento', 'config.usuario', 'config.perfil', 'config.acesso',
  'config.credenciais', 'auditoria', 'parte', 'catalogo.item', 'catalogo.preco',
  'fin.plano_contas', 'fin.centro_custo', 'atena.auditoria', 'atena.acesso', 'crm.contato',
]);

/**
 * [ADR-069 §13.24 / #891 D-nivel-tenant-consultar] Espelha `PermissaoDef.usoPorQualquerUnidade`
 * do catálogo backend (`permissoes.catalogo.ts`) — códigos EXATOS (não recurso inteiro) de
 * nível TENANT que o Filipe decidiu tratar como "uso" (ler/consultar num seletor, criar um
 * registro do dia a dia) em vez de "gestão" tenant-wide: contam com atribuição em QUALQUER
 * unidade, mesma regra do ramo ESTABELECIMENTO abaixo. Mesma dívida de sincronização manual de
 * `RECURSOS_NIVEL_TENANT` (comentário acima) — se um código novo ganhar `usoPorQualquerUnidade`
 * no backend, esta lista precisa da mesma linha, senão o frontend fica mais restritivo que o
 * backend permite (esconde algo que a rota já libera — seguro, mas regressão de UX).
 */
const CODIGOS_USO_QUALQUER_UNIDADE = new Set([
  // [ADR-069 §13.25 / #891 #953] `crm.contato:editar` entrou nesta entrega — era só ler/criar.
  'crm.contato:ler', 'crm.contato:criar', 'crm.contato:editar',
  'parte:ler', 'parte:consultar',
  'catalogo.item:ler', 'catalogo.item:consultar',
  'fin.plano_contas:ler', 'fin.plano_contas:consultar',
  'fin.centro_custo:ler', 'fin.centro_custo:consultar',
]);

function ehNivelTenant(codigo: string): boolean {
  const recurso = codigo.slice(0, codigo.lastIndexOf(':'));
  return RECURSOS_NIVEL_TENANT.has(recurso);
}

/** Exportada só para teste direto (`use-permissao.spec.ts`) — sem infra de render de hooks no
 *  frontend hoje (vitest roda em `environment: 'node'`, sem @testing-library/react). */
export function temCodigo(acesso: AcessoResumo, codigo: string, estabelecimentoId?: number | null): boolean {
  if (acesso.permissoes.tenant_wide.includes(codigo)) return true;
  // nível TENANT: só tenant_wide conta, nunca por_estabelecimento — exceto ação de "uso"
  // marcada em CODIGOS_USO_QUALQUER_UNIDADE (§13.24), que cai no mesmo ramo do ESTABELECIMENTO.
  if (ehNivelTenant(codigo) && !CODIGOS_USO_QUALQUER_UNIDADE.has(codigo)) return false;
  if (estabelecimentoId != null) {
    return (acesso.permissoes.por_estabelecimento[String(estabelecimentoId)] ?? []).includes(codigo);
  }
  // Sem unidade específica: conta em qualquer uma onde o usuário tenha o código.
  return Object.values(acesso.permissoes.por_estabelecimento).some((lista) => lista.includes(codigo));
}

/**
 * [ADR-069 §13.27 / #948] Lógica pura de `pode()` — extraída pra teste direto
 * (`use-permissao.spec.ts`), mesmo motivo de `temCodigo` acima (sem infra de render de hooks
 * no frontend hoje). `acesso: null` cobre os dois casos que viram fail-closed nesta entrega:
 * `/auth/me` ainda não respondeu, OU respondeu e o usuário não tem nenhuma atribuição RBAC —
 * `useAcessoStore.acesso` é `null` nos dois (`temAtribuicaoRbac` só é `true` com `atribuicoes`
 * não-vazio). SUPER_ADMIN (`acesso.plataforma`) é a ÚNICA exceção — sempre `true`.
 */
export function podeComAcesso(
  acesso: AcessoResumo | null,
  codigo: string | string[],
  estabelecimentoId?: number | null,
): boolean {
  if (acesso?.plataforma) return true; // SUPER_ADMIN — fora do RBAC do tenant (§10)
  if (!temAtribuicaoRbac(acesso) || !acesso) return false; // [ADR-069 §13.27] fail-closed
  const lista = Array.isArray(codigo) ? codigo : [codigo];
  return lista.some((c) => temCodigo(acesso, c, estabelecimentoId));
}

export interface UsePermissaoResultado {
  /**
   * [ADR-069 §13.27 / #948] true se o usuário tiver AO MENOS UM dos códigos informados (aceita
   * string única ou array — "basta ter uma"). SUPER_ADMIN (`acesso.plataforma`) sempre dá
   * `true` — está fora do RBAC do tenant por definição (§10), nunca é "sem atribuição".
   * Usuário de TENANT sem nenhuma atribuição RBAC (ou `acesso` ainda não carregado) agora dá
   * `false` — fail-CLOSED. Até esta entrega dava sempre `true` ("compatibilidade — regra 2 do
   * F4.3", pensada pro período pré-backfill); a rede de segurança do deploy (backfill rodado
   * ANTES do código novo subir, `scripts/deploy-qa.sh`/`deploy-prod.sh`) torna esse estado
   * impossível em QA/PROD — sobra só o instante de boot antes do `/auth/me` responder, onde o
   * menu fica momentaneamente sem itens com `permissao` (mesmo tratamento das 4 telas globais,
   * que nunca dependem de `pode()`).
   */
  pode: (codigo: string | string[], estabelecimentoId?: number | null) => boolean;
  /** true quando o usuário tem pelo menos uma UsuarioAcesso — usado por quem precisa saber
   *  se o filtro de permissão está "ligado" pra esse usuário (ex.: header). */
  temAtribuicao: boolean;
  acesso: AcessoResumo | null;
}

export function usePermissao(): UsePermissaoResultado {
  const acesso = useAcessoStore((s) => s.acesso);
  const temAtribuicao = temAtribuicaoRbac(acesso);

  function pode(codigo: string | string[], estabelecimentoId?: number | null): boolean {
    return podeComAcesso(acesso, codigo, estabelecimentoId);
  }

  return { pode, temAtribuicao, acesso };
}
